import { v4 as uuidv4 } from 'uuid'
import { stoppagesAjax } from '../ajax/StoppagesAjax'
import { convertDatesToUTC } from '../ajax/controller/AjaxController'
import { store } from '../state/store'
import type { ActionResponse } from '../DTOs/Common/ErrorResponse'
import type { StoppageDto } from '../DTOs/Stoppage/StoppageDto'
import { computeShiftId } from './computeShiftId'
import { enqueue } from './queueOps'
import { cachedPlantFor, patchCachedStoppages } from './patchCachedStoppages'
import { nextTempId } from './tempId'
import { isOnlineNow } from './connectivity'

function currentShiftId(): number {
  const { selectedDate, selectedShift } = store.getState().shift
  return computeShiftId(selectedDate, selectedShift)
}

// Note: convertDatesToUTC must run BEFORE the JSON round-trip. JSON.stringify calls Luxon's
// toJSON, which emits the zone offset ("2026-07-28T18:00:00+07:00"), and once the DateTime is a
// string the converter in enqueue can no longer normalise it. The API turns an offset into a
// server-local DateTime, so a queued stoppage was stored shifted by the difference between the
// mine zone and the server's - 18:00 mine saved as 19:00.
function serialize(obj: unknown): unknown {
  return JSON.parse(JSON.stringify(convertDatesToUTC(obj)))
}

// Note: the server ignores these nested objects on insert/update but still model-validates them, and
// its own responses come back with nulls in fields it treats as required (plant.owner), so echoing a
// row straight back fails with a 400. Send scalars only.
function withoutNavProps(dto: StoppageDto): StoppageDto {
  const { plant: _p, stoppageType: _st, stoppageSubType: _sst, location: _l, ...fields } = dto
  return fields as StoppageDto
}

// Note: callers need to know which path a mutation took, and they cannot ask the connectivity
// helpers: reachability is only learned from a request that has already failed, so just after the
// network drops isOnlineNow() still says yes. A caller that guessed wrong skipped the optimistic
// decoration and the cache patches - the row appeared with no stoppage type and was gone on remount.
export type QueuedResponse<T> = ActionResponse<T> & { queued: true };

export function wasQueued(result: ActionResponse<unknown> | { queued?: boolean }): boolean {
  return (result as { queued?: boolean }).queued === true;
}

export const stoppagesMutations = {
  async addStoppage(dto: StoppageDto) {
    if (isOnlineNow()) return stoppagesAjax.addStoppage(withoutNavProps(dto))
    const tempId = uuidv4()
    const tempStoppageId = await nextTempId()
    await enqueue({
      entity: 'stoppage',
      operation: 'create',
      shiftId: currentShiftId(),
      tempId,
      entityId: tempStoppageId,
      data: serialize(dto),
    })
    // Note: attach the machine here rather than in each caller. Every list that shows a stoppage
    // renders the machine (and colours it by department) from this nested plant, and a row that
    // reaches a list without one renders as an empty cell. Doing it in the mutation means every
    // creation path gets it - the nested table, the multiple-stoppage batch, and anything added later.
    const queuedAdd: QueuedResponse<StoppageDto> = {
      success: true,
      queued: true,
      data: {
        ...dto,
        plant: dto.plant ?? cachedPlantFor(dto.plantId),
        stoppageId: tempStoppageId,
      } as StoppageDto,
    }
    return queuedAdd
  },

  async updateStoppage(dto: StoppageDto) {
    if (isOnlineNow()) return stoppagesAjax.updateStoppage(withoutNavProps(dto))
    await enqueue({
      entity: 'stoppage',
      operation: 'update',
      shiftId: currentShiftId(),
      entityId: dto.stoppageId,
      rowVersion: dto.rowVersion,
      data: serialize(withoutNavProps(dto)),
    })
    // Note: return the merged cached row, not the caller's dto. The dto is the edited scalars only,
    // and handing it back made the table replace a full row with a partial one - entering a time out
    // wiped the Downtime Type off screen. Online the server's response carries the nav objects, which
    // is why this only showed offline.
    const merged = patchCachedStoppages(dto)
    const queuedUpdate: QueuedResponse<StoppageDto> = { success: true, queued: true, data: merged ?? dto }
    return queuedUpdate
  },

  async deleteStoppage(id: number, rowVersion?: string) {
    if (isOnlineNow()) return stoppagesAjax.deleteStoppage(id)
    await enqueue({
      entity: 'stoppage',
      operation: 'delete',
      shiftId: currentShiftId(),
      entityId: id,
      rowVersion,
    })
    const queuedDelete: QueuedResponse<null> = { success: true, queued: true, data: null }
    return queuedDelete
  },
}
